import React, { useEffect, useRef, useState } from 'react';
import styles from '../style/styles/ChatInput.module.css';
import { useResetQuery, useSendMessageWithWebSocket, useSendPDPMessage } from '../services/queries/chatbotQueries';
import { useChatStateStore, useMessagesStore, useProductsStore, useChatbotStore, useUserStore } from '../zustand/store';
import { useSendMessage } from '../services/queries/chatbotQueries';
import { sendInputFocusStatus, sendResetState, sendUserSentMessageState, updateVisitHistory } from '../utils/utils';
import { contents } from '../data/contents';

const ChatInput = () => {
    const { examples, clearExamples } = useProductsStore();
    const { chatbot } = useChatbotStore();
    const { isMobileDevice } = useUserStore();
    const { isChatbotPending, setIsChatbotPending, isStreaming, setIsStreaming, isFormSubmitted, displayLocation, currentPage, isChatOpen, language, isInConsoleTour, clearIsInConsoleTour } = useChatStateStore();
    const { mutateAsync: resetQuery } = useResetQuery();
    const { mutate: sendMessageWithWebSocket } = useSendMessageWithWebSocket();
    const { mutate: sendPDPMessage } = useSendPDPMessage();
    const [userInput, setUserInput] = useState('');
    const { isPending } = useSendMessage();
    const { messages } = useMessagesStore();
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [isResetHovered, setIsResetHovered] = useState(false);
    const [isSendHovered, setIsSendHovered] = useState(false);
    const [isComposing, setIsComposing] = useState(false);
    const wantSendRef = useRef(false);
    const suggestionsScrollRef = useRef(null);
    const animationRef = useRef(null);
    const resumeTimeoutRef = useRef(null);
    const scrollSpeedPixelsPerSecond = 18;
    const [finishedScroll, setFinishedScroll] = useState(false);  // do not start scroll animation again if it is already finished
    const inputFocusRef = useRef(null);
    const minTextareaHeight = 19;
    const maxInputLines = 3;


    useEffect(() => {
        if (isInConsoleTour) {
            setUserInput('배송 정책 가이드라인 같이 써보자.');
        }
    }, [isInConsoleTour]);

    useEffect(() => {
        if (isMobileDevice) return;
        if (isFormSubmitted === 'ready') return;
        if (!isStreaming && !isChatbotPending) {
            inputFocusRef.current.focus();
        }
    }, [isInputFocused, isStreaming, isChatbotPending]);

    // Clean up on unmount
    useEffect(() => {
        return () => {
            if (resumeTimeoutRef.current) {
                clearTimeout(resumeTimeoutRef.current);
            }
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, []);
    
    useEffect(() => {
        if (isChatOpen) {
            checkOverflow();
            window.addEventListener('resize', checkOverflow);
        }
        return () => {
            window.removeEventListener('resize', checkOverflow);
            handlePauseAnimation();
        };
    }, [messages, examples, isChatOpen]);

    const checkOverflow = () => {
        if (suggestionsScrollRef.current) {
            const scrollContentWidth = suggestionsScrollRef.current.scrollWidth;
            const windowWidth = window.innerWidth;
            const isOverflowing = (scrollContentWidth + 32) > windowWidth;
            
            if (isOverflowing) {
                if (animationRef.current) {
                    cancelAnimationFrame(animationRef.current);
                }
                handleResumeAnimation();
            } else {
                handlePauseAnimation();
            }
        }
    };

    const handleUserInputChange = (e) => {
        setUserInput(e.target.value);
        handleTextareaResize(e.target);
    }

    const handleTextareaResize = (textarea) => {
        textarea.style.height = 'auto';
        
        const minHeight = minTextareaHeight;
        const maxHeight = minTextareaHeight * maxInputLines;
        const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
        textarea.style.height = newHeight + 'px';
    }

    const handleKeyPress = async (e) => {
        if (e.key !== 'Enter') return;
        if (e.shiftKey) return;
        e.preventDefault();
        e.stopPropagation();
        if (isPending || userInput.trim() === '' || isChatbotPending || isStreaming) return;

        if (isComposing || e.nativeEvent.isComposing) {
            wantSendRef.current = true;
        } else {
            handleInputBlur();
            setIsStreaming(true);
            sendUserSentMessageState(true);
            sendMessageWithWebSocket({ 
                messageInput: userInput, 
                isChatbot: true, 
                preAnswers: [],
            });
            updateVisitHistory(currentPage);
            setTimeout(() => setUserInput(''), 0);
            // Reset textarea height
            if (inputFocusRef.current) {
                inputFocusRef.current.style.height = `${minTextareaHeight}px`;
            }
            setIsChatbotPending(true);
        }
    };

    const handleComposingStart = (e) => {
        setIsComposing(true);
    }
    const handleComposingEnd = (e) => {
        setIsComposing(false);

        if (wantSendRef.current) {
            wantSendRef.current = false;
            requestAnimationFrame(() => {
                setTimeout(() => setUserInput(''), 0);
                // Reset textarea height
                if (inputFocusRef.current) {
                    inputFocusRef.current.style.height = `${minTextareaHeight}px`;
                }
            });
        }
    }

    const handleReset = () => {
        if (isChatbotPending) return;
        if (isStreaming) return;
        
        resetQuery().then(() => {
            setUserInput('');
            // Reset textarea height
            if (inputFocusRef.current) {
                inputFocusRef.current.style.height = `${minTextareaHeight}px`;
            }
            clearIsInConsoleTour();
            sendResetState();
            // send PDP message with chatType 'greeting'
            if (displayLocation === 'PRODUCT_DETAIL') {
                sessionStorage.removeItem('visitHistory');
                sendPDPMessage({ chatType: 'renew' });
            }
            /* when reset, reset suggestions scroll too */
            if (suggestionsScrollRef.current?.parentElement) {
                if (resumeTimeoutRef.current) {
                    clearTimeout(resumeTimeoutRef.current);
                }
                if (animationRef.current) {
                    cancelAnimationFrame(animationRef.current);
                }
                suggestionsScrollRef.current.parentElement.scrollLeft = 0;
                setFinishedScroll(false);
                checkOverflow();
            }
        }).catch(error => {
            console.error('Error during resetQuery:', error);
        });
    }

    const handleInputFocus = (e) => {
        setIsInputFocused(true);
        sendInputFocusStatus(true);
        if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.sendInputFocusState) {
            window.webkit.messageHandlers.sendInputFocusState.postMessage(`input focused`);
        } else {
            // console.warn('webkit.messageHandlers is not available');
        }

        // AOS SDK로 메시지 전달
        if (window.Android && window.Android.sendInputFocusState) {
            window.Android.sendInputFocusState('input focused');
        } else {
            // console.warn('Android interface is not available');
        }
    }

    const handleInputBlur = () => {
        setIsInputFocused(false);
        sendInputFocusStatus(false);
        if (isMobileDevice) {
            inputFocusRef.current.blur();
        }
    }

    const animateScroll = (startTime) => {
        if (!suggestionsScrollRef.current?.parentElement) return;

        const currentTime = Date.now();
        const elapsedTime = currentTime - startTime;
        const scrollPosition = (elapsedTime / 1000) * scrollSpeedPixelsPerSecond;

        suggestionsScrollRef.current.parentElement.scrollTo({
            left: scrollPosition,
            behavior: 'auto'
        });

        if (scrollPosition < suggestionsScrollRef.current.scrollWidth + 32 - window.innerWidth) {
            animationRef.current = requestAnimationFrame(() => animateScroll(startTime));
        } else {
            setFinishedScroll(true);
            handlePauseAnimation();
        }
    };

    useEffect(() => {
        window.addEventListener('message', (event) => {
            if (event.data.type === 'resetQuery') {
                handleReset();
            }
        });
        return () => {
            window.removeEventListener('message', (event) => {
                if (event.data.type === 'resetQuery') {
                    handleReset();
                }
            });
        };
    }, []);

    const handleResumeAnimation = () => {
        if (finishedScroll) return;
        // Clear any existing timeout first
        if (resumeTimeoutRef.current) {
            clearTimeout(resumeTimeoutRef.current);
        }
        resumeTimeoutRef.current = setTimeout(() => {
            if (suggestionsScrollRef.current?.parentElement) {
                const currentScroll = suggestionsScrollRef.current.parentElement.scrollLeft;
                const startTime = Date.now() - (currentScroll / scrollSpeedPixelsPerSecond * 1000);
                animateScroll(startTime);
            }
        }, 2000);
    };

    const handlePauseAnimation = () => {
        if (resumeTimeoutRef.current) {
            clearTimeout(resumeTimeoutRef.current);
        }
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
        }
    };

    return (
        <div className={styles.ChatInput}>
            <div 
                className={`${isMobileDevice ? styles.ChatInput__suggestions__mobile : styles.ChatInput__suggestions__desktop}`}
                // Mouse events for scrollbar interaction
                onMouseDown={() => {
                    if (!isMobileDevice) {
                        handlePauseAnimation();
                    }
                }}
                onMouseUp={() => {
                    if (!isMobileDevice) {
                        handleResumeAnimation();
                    }
                }}
                onMouseLeave={() => {
                    if (!isMobileDevice) {
                        handleResumeAnimation();
                    }
                }}
            >
                <div 
                    className={styles.ChatInput__suggestions__scrollContent} 
                    ref={suggestionsScrollRef}
                    // Mouse events for desktop
                    onMouseEnter={handlePauseAnimation}
                    onMouseLeave={handleResumeAnimation}
                    // Touch events for mobile
                    onTouchStart={handlePauseAnimation}
                    onTouchEnd={handleResumeAnimation}
                    // Scroll events for both
                    onScroll={(e) => {
                        e.stopPropagation();
                        handlePauseAnimation();
                        handleResumeAnimation();
                    }}
                >
                    {
                        messages.length === 0 ?
                        !isChatbotPending && !isStreaming && chatbot?.examples?.map((example, index) => (
                            <div
                            key={index}
                            className={styles.ChatInput__suggestions__item}
                            onClick={(e) => {
                                e.stopPropagation();
                            }}
                        >
                            <p
                                className='h3'
                                onClick={(e) => {
                                    setIsStreaming(true);
                                    sendUserSentMessageState(true);
                                    sendMessageWithWebSocket({ messageInput: example, isChatbot: false, preAnswers: [] });
                                    setIsChatbotPending(true);
                                    sendInputFocusStatus(true);
                                    clearExamples();
                                }}
                            >{example}</p>
                        </div>))
                        :
                        !isChatbotPending && !isStreaming && examples?.map((example, index) => (
                            <div
                                key={index}
                                className={styles.ChatInput__suggestions__item}
                                onClick={(e) => {
                                    e.stopPropagation();
                                }}
                            >
                                <p
                                    className='h3'
                                    onClick={(e) => {
                                        setIsStreaming(true);
                                        sendUserSentMessageState(true);
                                        sendMessageWithWebSocket({ messageInput: example, isChatbot: false, preAnswers: [] });
                                        setIsChatbotPending(true);
                                        sendInputFocusStatus(true);
                                        clearExamples();
                                        updateVisitHistory(currentPage);
                                    }}
                                >{example}</p>
                            </div>
                        ))
                    }
                </div>
            </div>
            <div className={styles.ChatInput__inputContainer}>
                <div className={styles.ChatInput__inputContainer__wrapper}>
                    <img
                        src={`${isChatbotPending || isStreaming ? '/img/units/refresh-disabled.png' : (isResetHovered ? '/img/units/refresh-hover.png' : '/img/units/refresh-active.png')}`}
                        // className={`${styles.resetIcon} ${isChatbotPending && 'non-clickable'}`} 
                        className={`${styles.ChatInput__resetIcon} ${(isChatbotPending || isStreaming) && 'non-clickable'}`}
                        alt="Reset input"
                        onClick={handleReset}
                        onMouseEnter={() => !isChatbotPending && setIsResetHovered(true)}
                        onMouseLeave={() => !isChatbotPending && setIsResetHovered(false)}
                    />
                    <div className='spacing-8' />
                    <div 
                        className={styles.ChatInput__inputWrapper}
                    >
                        <textarea
                            ref={inputFocusRef}
                            name='chatInput'
                            className={styles.ChatInput__inputWrapper__input}
                            placeholder={contents.chatInput[language].placeholder}
                            value={userInput}
                            onChange={handleUserInputChange}
                            onKeyDown={handleKeyPress}
                            onCompositionStart={handleComposingStart}
                            onCompositionEnd={handleComposingEnd}
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            rows={1}
                        />
                        <div className='spacing-8'/>
                        <img
                            src={`${(isChatbotPending || isStreaming) ? '/img/units/loading-spinner-28px.gif' : (userInput === '' ? '/img/units/send-disabled.png' : (isSendHovered ? '/img/units/send-hover.png' : '/img/units/send-active.png'))}`}
                            className={`${styles.ChatInput__sendIcon} ${(isChatbotPending || (userInput === '')) && 'non-clickable'}`}
                            alt="Send message"
                            onClick={
                                () => {
                                    if (userInput.trim() === '') return;
                                    if (isChatbotPending) return;
                                    if (isStreaming) return;
                                    setIsStreaming(true);
                                    sendUserSentMessageState(true);
                                    sendMessageWithWebSocket({ messageInput: userInput, isChatbot: false, preAnswers: [] });
                                    updateVisitHistory(currentPage);
                                    setUserInput('');
                                    // Reset textarea height
                                    if (inputFocusRef.current) {
                                        inputFocusRef.current.style.height = `${minTextareaHeight}px`;
                                    }
                                    setIsChatbotPending(true);
                                    if (isInConsoleTour) {
                                        window.parent.postMessage({
                                            type: 'sendButtonClickInTour',
                                        }, process.env.REACT_APP_BASE_URL);
                                    }
                                }
                            }
                            onMouseEnter={() => !isChatbotPending && userInput !== '' && setIsSendHovered(true)}
                            onMouseLeave={() => !isChatbotPending && userInput !== '' && setIsSendHovered(false)}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ChatInput;